import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import {
  createRoyaltyChecksSearchAtom,
  type RoyaltyChecksSearchParams,
} from 'pages/royalty-checks/list/atoms';
import {
  royaltyCheckColumns,
  type RoyaltyCheckRow,
} from 'pages/royalty-checks/list/columns';
import { useRoyaltyCheckRowSubComponent } from 'pages/royalty-checks/list/line-items-expansion';
import { SORT_FIELD_MAP } from 'pages/royalty-checks/list/utils';

type RCRow = RoyaltyCheckRow;

/**
 * Scopes the royalty-checks query to a single entity or a single well. Each
 * key maps to the matching `RoyaltyChecksSearchParams` filter.
 */
export type RoyaltyChecksScope = { entityId: number } | { lineUwi: string };

const DEFAULT_SORT_ATTRIBUTE = 'checkIssueDate';

interface Props {
  scope: RoyaltyChecksScope;
  persistenceKey: string;
}

// Shared royalty-checks table used on the entity and well detail pages. Both
// render the same columns and line-item sub-rows; they differ only in the
// scope filter applied to the query and the persistence key.
const RoyaltyChecksTable: FC<Props> = ({ scope, persistenceKey }) => {
  const { searchAtom } = useMemo(() => createRoyaltyChecksSearchAtom(), []);
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  const defaultParams: RoyaltyChecksSearchParams = useMemo(
    () => ({
      page: 1,
      pageSize: 100,
      sortAttribute: DEFAULT_SORT_ATTRIBUTE,
      sortDirection: 'DESC',
      ...scope,
    }),
    [scope]
  );

  const [params, setParams] =
    useState<RoyaltyChecksSearchParams>(defaultParams);

  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  const refetch = useCallback((state: TableState) => {
    const sort = state.sorting[0];
    setParams((curr) => ({
      ...curr,
      page: state.page,
      pageSize: state.pageSize,
      sortAttribute: sort
        ? (SORT_FIELD_MAP[sort.id] ?? sort.id)
        : DEFAULT_SORT_ATTRIBUTE,
      sortDirection: sort?.desc === false ? 'ASC' : 'DESC',
      text: state.globalFilter || undefined,
    }));
  }, []);

  const renderLineItems = useRoyaltyCheckRowSubComponent<RCRow>();

  const results = searchResult.data?.results ?? [];
  const totalCount = searchResult.data?.totalCount ?? 0;

  return (
    <DataTable<RCRow>
      data={results as RCRow[]}
      columns={royaltyCheckColumns as DataTableColumnDef<RCRow>[]}
      getRowId={(row) => String(row.id)}
      initialTableState={{
        sortColumn: DEFAULT_SORT_ATTRIBUTE,
        sortDirection: 'desc',
      }}
      persistenceKey={persistenceKey}
      config={{ columnFilters: false, columnReordering: false }}
      isLoading={searchResult.loading}
      error={searchResult.error ?? null}
      scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
      onPaginationChange={refetch}
      onSortChange={refetch}
      onGlobalSearchChange={refetch}
      searchPlaceholder="Search Royalty Checks"
      renderRowSubComponent={renderLineItems}
    />
  );
};

export { RoyaltyChecksTable };
